import React from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../components/Navbar';
import Footer from '../components/Footer';
import { LanguageContext } from '../contexts/LanguageContext';
import { Helmet } from 'react-helmet';
import EditableText from '../components/EditableText';
import axios from 'axios';

type PlantType = {
  code: string; // e.g. 'LF'
  name_de: string;
  name_en: string;
  description_de?: string | null;
  description_en?: string | null;
};

type Activity = {
  name_de: string;
  shortName?: string | null;
  internalName: string; // stable key
  description_de?: string | null;
  name_en?: string;
  description_en?: string | null;
};

type Plant = {
  id: string; // client temp id
  name: string;
  shortName: string;
  icon?: string; // filename or URL
  typeCode?: string; // references PlantType.code
  chargeWeightTons?: number; // tons
  description?: string;
  activities: string[]; // list of Activity.internalName
};

type CustomerSetup = {
  customerId?: string; // returned from backend on save
  customerName: string;
  email: string;
  phone?: string;
  companyName: string;
  notes?: string;
  plants: Plant[];
};

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Fallback defaults if backend endpoints are not available
const DEFAULT_PLANT_TYPES: PlantType[] = [
  { code: 'General', name_de: 'Allgemeine Anlage', name_en: 'General', description_de: 'Allgemeine Anlage', description_en: 'General Plant' },
  { code: 'AOD', name_de: 'AOD', name_en: 'AOD', description_de: 'Argon Oxygen Decarburization', description_en: 'Argon Oxygen Decarburization' },
  { code: 'BF', name_de: 'BF', name_en: 'BF', description_de: 'Hochofen', description_en: 'Blast Furnace' },
  { code: 'BOF', name_de: 'BOF', name_en: 'BOF', description_de: 'Konverter', description_en: 'Basic Oxygen Furnace' },
  { code: 'CC', name_de: 'CC', name_en: 'CC', description_de: 'Stranggießanlage', description_en: 'Continuous Casting' },
  { code: 'EAF', name_de: 'EAF', name_en: 'EAF', description_de: 'Lichtbogenofen', description_en: 'Electric Arc Furnace' },
  { code: 'ESR', name_de: 'ESR', name_en: 'ESR', description_de: 'Elektroschlacke-Umschmelzen', description_en: 'Electroslag Remelting' },
  { code: 'CP', name_de: 'GG', name_en: 'CP', description_de: 'Gießgrube', description_en: 'Casting Pit' },
  { code: 'HMD', name_de: 'HMD', name_en: 'HMD', description_de: 'Entschwefelung Roheisen', description_en: 'Hot Metal Desulphurization' },
  { code: 'HMTP', name_de: 'HMTP', name_en: 'HMTP', description_de: 'Roheisen-Transfergrube', description_en: 'Hot Metal Transfer Pit' },
  { code: 'IC', name_de: 'IC', name_en: 'IC', description_de: 'Blockguss', description_en: 'Ingot Casting' },
  { code: 'IF', name_de: 'IF', name_en: 'IF', description_de: 'Induktionsofen', description_en: 'Induction Furnace' },
  { code: 'IF-V', name_de: 'IF-V', name_en: 'IF-V', description_de: 'Induktionsofen mit Vakuumdeckel', description_en: 'Induction Furnace with Vacuum Lid' },
  { code: 'Crane', name_de: 'Kran', name_en: 'Crane', description_de: 'Kran', description_en: 'Crane' },
  { code: 'LF', name_de: 'LF', name_en: 'LF', description_de: 'Pfannenofen', description_en: 'Ladle Furnace' },
  { code: 'LT', name_de: 'LT', name_en: 'LT', description_de: 'Pfannendrehturm', description_en: 'Ladle Turret' },
  { code: 'Furnace', name_de: 'Ofen', name_en: 'Furnace', description_de: 'Ofen', description_en: 'Furnace' },
  { code: 'RH', name_de: 'RH', name_en: 'RH', description_de: 'RH-Anlage', description_en: 'RH Plant' },
  { code: 'SST', name_de: 'Spülstand', name_en: 'SST', description_de: 'Spülstand', description_en: 'Stirring Stand' },
  { code: 'SY', name_de: 'SY', name_en: 'SY', description_de: 'Schrottplatz', description_en: 'Scrapyard' },
  { code: 'VD', name_de: 'VD', name_en: 'VD', description_de: 'Vakuum Entgasung', description_en: 'Vacuum Degassing' },
  { code: 'VIM', name_de: 'VIM', name_en: 'VIM', description_de: 'Vakuum-Induktionsschmelzen', description_en: 'Vacuum Induction Melting' },
  { code: 'VOD', name_de: 'VOD', name_en: 'VOD', description_de: 'Vakuum Sauerstoff-Entkohlung', description_en: 'Vacuum Oxygen Decarburization' },
];

const DEFAULT_ACTIVITIES: Activity[] = [
  { name_de: 'VOD', internalName: 'VacuumOxygenDecarburization', description_de: 'Vakuum Sauerstoff Entkohlung', name_en: 'VOD', description_en: 'Vacuum Oxygen Decarburization' },
  { name_de: 'VD', internalName: 'VacuumDegassing', description_de: 'Vakuum Entgasung', name_en: 'VD', description_en: 'Vacuum Degassing' },
  { name_de: 'Vakuum', internalName: 'Vacuum', description_de: 'Vakuum', name_en: 'Vacuum', description_en: 'Vacuum' },
  { name_de: 'Temperatur-Messung', shortName: 'Temp', internalName: 'TemperatureMeasurement', description_de: 'Temperaturmessung von DigiTemp automatisch aus Level-1', name_en: 'Temperature Measurement', description_en: 'Temperature measurement' },
  { name_de: 'Abstich', internalName: 'Tapping', description_de: 'Abstich', name_en: 'Tapping', description_en: 'Tapping' },
  { name_de: 'Spüllanze N2', internalName: 'StirringLanceN2', description_de: 'Spüllanze Stickstoff', name_en: 'Stirring Lance N2', description_en: 'Stirring lance N2' },
  { name_de: 'Spülgas N2', internalName: 'StirringGasN2', description_de: 'Spülgas Stickstoff', name_en: 'Stirring Gas N2', description_en: 'Stirring gas N2' },
  { name_de: 'Spülgas Ar', internalName: 'StirringGasAr', description_de: 'Spülgas Argon', name_en: 'Stirring Gas Ar', description_en: 'Stirring gas Ar' },
  { name_de: 'Probenahme', shortName: 'Probe', internalName: 'Sample', description_de: 'Probenahme', name_en: 'Sampling', description_en: 'Sample' },
  { name_de: 'Lastbegrenzung', shortName: 'Last', internalName: 'PowerDemand', description_de: 'Begrenzung Energiebezug', name_en: 'Power Demand Limit', description_en: 'Limit power demand' },
  { name_de: 'Drahtzugabe', shortName: 'Draht', internalName: 'MaterialAdditionWire', description_de: 'Drahtzugabe', name_en: 'Wire Addition', description_en: 'Wire addition' },
  { name_de: 'Materialzugabe', shortName: 'Material', internalName: 'MaterialAddition', description_de: 'Materialzugabe Aggregat', name_en: 'Material Addition', description_en: 'Material addition' },
  { name_de: 'Hydrismessung', shortName: 'Hydris', internalName: 'HydrisMeasurement', description_de: 'Hydrismessung', name_en: 'Hydris Measurement', description_en: 'Hydris measurement' },
  { name_de: 'Freibord', internalName: 'Freeboard', description_de: 'Freibordmessung', name_en: 'Freeboard', description_en: 'Freeboard' },
  { name_de: 'Fluten', internalName: 'Flooding', description_de: 'Vakuumgefäß fluten', name_en: 'Flooding', description_en: 'Flooding' },
  { name_de: 'Celox-Messung', shortName: 'Celox', internalName: 'EMFMeasurement', description_de: 'Celox-Messung', name_en: 'EMF Measurement', description_en: 'EMF measurement' },
  { name_de: 'Schmelzen', internalName: 'ElectricMelting', description_de: null, name_en: 'Melting', description_en: null },
  { name_de: 'Heizen', internalName: 'ElectricHeatingLF', description_de: 'Heizen Pfannenofen', name_en: 'Heating', description_en: 'Heating (LF)' },
  { name_de: 'Abschlacken', shortName: 'Absch', internalName: 'Deslagging', description_de: 'Abschlacken', name_en: 'Deslagging', description_en: 'Deslagging' },
  { name_de: 'Deckelfahrt', internalName: 'CoverMoving', description_de: 'Deckelfahrt', name_en: 'Cover Moving', description_en: 'Cover moving' },
  { name_de: 'Cojet Brenner', internalName: 'CojetMeltingEAF', description_de: 'Brenner (Lichtbogenofen)', name_en: 'Cojet Burner', description_en: 'Burner (EAF)' },
  { name_de: 'Giessen', internalName: 'Casting', description_de: 'Gießen', name_en: 'Casting', description_en: 'Casting' },
  { name_de: 'Blasen', internalName: 'Blowing', description_de: 'Blasen', name_en: 'Blowing', description_en: 'Blowing' },
  { name_de: 'Behälter entladen', shortName: 'Behälter', internalName: 'BasketCharging', description_de: 'Behälter entladen', name_en: 'Basket Charging', description_en: 'Basket charging' },
];

type IconItem = { name: string; src: string };

const tryLoadLocalIcons = (): IconItem[] => {
  try {
    // Note: This folder may not exist yet. If absent, we return empty list gracefully.
    // @ts-ignore - require.context is provided by webpack in CRA
    const ctx = require.context('../images/PlantIcons', false, /\.svg$/);
    return ctx.keys().map((key: string) => ({ name: key.replace('./', ''), src: ctx(key).default || ctx(key) }));
  } catch (e) {
    return [];
  }
};

const SetupPage: React.FC = () => {
  const { language } = React.useContext(LanguageContext);
  const navigate = useNavigate();
  const [plantTypes, setPlantTypes] = React.useState<PlantType[]>([]);
  const [activities, setActivities] = React.useState<Activity[]>([]);
  const [icons, setIcons] = React.useState<IconItem[]>(tryLoadLocalIcons());
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successId, setSuccessId] = React.useState<string | null>(null);

  const [form, setForm] = React.useState<CustomerSetup>({
    customerName: '',
    email: '',
    phone: '',
    companyName: '',
    notes: '',
    plants: [],
  });
  const [selectedPlantIndex, setSelectedPlantIndex] = React.useState<number>(-1);
  const BOOKING_LINK_DE = process.env.REACT_APP_MS_BOOKINGS_URL_DE || 'https://outlook.office.com/book/meltshop365Registrierung@grips-solutions.com/?ismsaljsauthenabled';
  const BOOKING_LINK_EN = process.env.REACT_APP_MS_BOOKINGS_URL_EN || 'https://outlook.office.com/book/meltshop365Registrierung@grips-solutions.com/?ismsaljsauthenabled';
  const BOOKING_LINK = language === 'de' ? BOOKING_LINK_DE : BOOKING_LINK_EN;

  React.useEffect(() => {
    let mounted = true;
    const fetchMeta = async () => {
      try {
        setError(null);
        // fetch plant types
        const typesPromise = axios.get(`${API_BASE}/plant-types`).then(r => r.data).catch(() => DEFAULT_PLANT_TYPES);
        // fetch activities
        const actsPromise = axios.get(`${API_BASE}/activities`).then(r => r.data).catch(() => DEFAULT_ACTIVITIES);
        // fetch icons list if backend provides it
        const iconsPromise = axios.get(`${API_BASE}/icons`).then(r => r.data as IconItem[]).catch(() => icons);
        const [typesRes, actsRes, iconsRes] = await Promise.all([typesPromise, actsPromise, iconsPromise]);
        if (!mounted) return;
        setPlantTypes(typesRes && Array.isArray(typesRes) ? typesRes : DEFAULT_PLANT_TYPES);
        setActivities(actsRes && Array.isArray(actsRes) ? actsRes : DEFAULT_ACTIVITIES);
        if (icons.length === 0 && Array.isArray(iconsRes)) setIcons(iconsRes);
      } catch (e) {
        if (!mounted) return;
        setPlantTypes(DEFAULT_PLANT_TYPES);
        setActivities(DEFAULT_ACTIVITIES);
      }
    };
    fetchMeta();
    return () => { mounted = false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = (de: string, en: string) => (language === 'de' ? de : en);

  const addPlant = () => {
    const newPlant: Plant = {
      id: `plant_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: '',
      shortName: '',
      activities: [],
    };
    setForm(prev => ({ ...prev, plants: [...prev.plants, newPlant] }));
    setSelectedPlantIndex(form.plants.length);
  };

  const removeSelectedPlant = () => {
    if (selectedPlantIndex < 0) return;
    setForm(prev => {
      const next = [...prev.plants];
      next.splice(selectedPlantIndex, 1);
      return { ...prev, plants: next };
    });
    setSelectedPlantIndex(-1);
  };

  const updateSelectedPlant = (patch: Partial<Plant>) => {
    if (selectedPlantIndex < 0) return;
    setForm(prev => {
      const next = [...prev.plants];
      next[selectedPlantIndex] = { ...next[selectedPlantIndex], ...patch } as Plant;
      return { ...prev, plants: next };
    });
  };

  const toggleActivity = (internalName: string) => {
    if (selectedPlantIndex < 0) return;
    const plant = form.plants[selectedPlantIndex];
    const set = new Set(plant.activities);
    if (set.has(internalName)) set.delete(internalName); else set.add(internalName);
    updateSelectedPlant({ activities: Array.from(set) });
  };

  const validate = (): string | null => {
    if (!form.customerName.trim()) return t('Bitte Kundennamen angeben.', 'Please provide customer name.');
    if (!form.companyName.trim()) return t('Bitte Firmenname angeben.', 'Please provide company name.');
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return t('Bitte gültige E-Mail angeben.', 'Please provide a valid email.');
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessId(null);
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/customer-setup`, form);
      const id = res?.data?.customerId || res?.data?.id || null;
      setSuccessId(id);
      if (id) {
        // Redirect to success page where ID and next steps are shown
        navigate(`/setup/success/${id}`, { state: { email: form.email } });
        return;
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Speichern fehlgeschlagen.', 'Failed to save.'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className='grips-bg d-flex flex-column min-vh-100'>



      {language === 'de' ? (
        <Helmet>
          <title>Setup & Konfiguration - meltshop365</title>
          <meta name="description" content="Erfassen Sie Ihre Firmendaten, damit wir Ihre Anwendung vorkonfigurieren können." />
        </Helmet>
      ) : (
        <Helmet>
          <title>Setup & Configuration - meltshop365</title>
          <meta name="description" content="Provide your company details so we can preconfigure your application." />
        </Helmet>
      )}
      <NavigationBar />

      <div className="text-center flex-grow-1">
        {language === 'de' ? (
          <h1 className="my-4 mt-5 text-white" id='700'><EditableText id='700' isH1 /></h1>
        ) : (
          <h1 className="my-4 mt-5 text-white" id='701'><EditableText id='701' isH1 /></h1>
        )}
        <div className="mx-auto my-4" style={{ width: '100px', borderBottom: '10px solid red' }}></div>

        <div className='container text-start'>
          {/* Alerts */}
          {error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}
          {successId && (
            <div className="alert alert-success" role="alert">
              {t('Erfolgreich gespeichert. Ihre ID: ', 'Saved successfully. Your ID: ')} {successId}
            </div>
          )}
          {/* Booking / Scheduling IFrame */}
          <iframe
            src={BOOKING_LINK}
            title='Booking'
            width='100%'
            // Using a viewport-based minHeight so it is clearly visible; height:100% didn't work because parent has no explicit height.
            style={{ minHeight: '75vh', border: '0' }}
            scrolling='yes'
            loading='lazy'
            referrerPolicy='no-referrer'
          />

          {
          // comment aout Plant Configuration for now
          
          false && <> 
          
   
                    {/* General Customer Info */}
          <div className='card bg-transparent border-0'>
            <div className='card-body'>
              <h2 className='text-white h4 mb-3'>{t('Kundendaten', 'Customer details')}</h2>
              <div className='row g-3'>
                <div className='col-md-6'>
                  <label className='form-label text-white'>{t('Name des Kunden', 'Customer name')}</label>
                  <input className='form-control' value={form.customerName} onChange={e => setForm({ ...form, customerName: e.target.value })} />
                </div>
                <div className='col-md-6'>
                  <label className='form-label text-white'>{t('E-Mail-Adresse', 'Email address')}</label>
                  <input className='form-control' type='email' value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className='col-md-6'>
                  <label className='form-label text-white'>{t('Telefonnummer', 'Phone number')}</label>
                  <input
                    className='form-control'
                    type='tel'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
                    placeholder={t('Nur Ziffern, z. B. 020180054510', 'Digits only, e.g. 020180054510')}
                  />
                  <div className='form-text text-light'>{t('Nur Zahlen erlaubt.', 'Numbers only.')}</div>
                </div>
                <div className='col-md-6'>
                  <label className='form-label text-white'>{t('Name der Firma', 'Company name')}</label>
                  <input className='form-control' value={form.companyName} onChange={e => setForm({ ...form, companyName: e.target.value })} />
                </div>
                <div className='col-12'>
                  <label className='form-label text-white'>{t('Allgemeine Beschreibung / Notizen', 'General description / notes')}</label>
                  <textarea className='form-control' rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
            </div>
          </div>

          <div className='mx-auto my-4' style={{ width: '100px', borderBottom: '10px solid red' }}></div>

          {/* Plants Master-Detail */}
          <div className='d-flex align-items-center mb-2'>
            <h2 className='text-white h4 m-0'>{t('Anlagen konfigurieren', 'Configure plants')}</h2>
          </div>
          <div className='row'>
            {/* Left: Plant list */}
            <div className='col-md-4 mb-3'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='text-white'>{t('Anlagen', 'Plants')}</span>
                <button type='button' className='btn btn-sm btn-danger' onClick={addPlant}>{t('Anlage hinzufügen', 'Add plant')}</button>
              </div>
              <ul className='list-group' style={{ position: 'sticky' as const, top: '1rem' }}>
                {form.plants.map((p, idx) => (
                  <li key={p.id} className={`list-group-item list-group-item-action d-flex align-items-center ${idx === selectedPlantIndex ? 'active' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedPlantIndex(idx)}>
                    <div className='me-2 d-inline-flex align-items-center justify-content-center rounded-circle' style={{ width: 32, height: 32, background: '#f8f9fa' }}>
                      {p.icon ? (
                        <img src={p.icon.startsWith('http') ? p.icon : (icons.find(i => i.name === p.icon)?.src || p.icon)} alt="icon" style={{ width: 18, height: 18 }} />
                      ) : (
                        <span className='text-muted'>+</span>
                      )}
                    </div>
                    <div className='flex-grow-1'>
                      <div className='fw-semibold'>{p.name || t('Neue Anlage', 'New plant')}</div>
                      <div className='small text-muted'>{p.shortName || ''}</div>
                    </div>
                    {p.typeCode && <span className={`badge ${idx === selectedPlantIndex ? 'bg-light text-dark' : 'bg-secondary'}`}>{p.typeCode}</span>}
                    {idx === selectedPlantIndex && (
                      <button type='button' className='btn btn-sm btn-outline-light ms-2' onClick={(e) => { e.stopPropagation(); removeSelectedPlant(); }}>{t('Entfernen', 'Remove')}</button>
                    )}
                  </li>
                ))}
                {form.plants.length === 0 && (
                  <li className='list-group-item'>{t('Noch keine Anlagen hinzugefügt.', 'No plants added yet.')}</li>
                )}
              </ul>
            </div>

            {/* Right: Plant details */}
            <div className='col-md-8'>
              {selectedPlantIndex >= 0 ? (
                <div className='card border-0 shadow-sm'>
                  <div className='card-body'>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <label className='form-label'>{t('Name der Anlage', 'Plant name')}</label>
                        <input className='form-control' value={form.plants[selectedPlantIndex].name} onChange={e => updateSelectedPlant({ name: e.target.value })} />
                      </div>
                      <div className='col-md-6'>
                        <label className='form-label'>{t('Kurzname / Abkürzung', 'Short name / abbreviation')}</label>
                        <input className='form-control' value={form.plants[selectedPlantIndex].shortName} onChange={e => updateSelectedPlant({ shortName: e.target.value })} />
                      </div>

                      <div className='col-md-6'>
                        <label className='form-label'>{t('Anlagen-Typ', 'Plant type')}</label>
                        <select className='form-select' value={form.plants[selectedPlantIndex].typeCode || ''} onChange={e => updateSelectedPlant({ typeCode: e.target.value || undefined })}>
                          <option value=''>{t('Bitte wählen…', 'Please select…')}</option>
                          {plantTypes.map(pt => (
                            <option key={pt.code} value={pt.code}>
                              {language === 'de' ? `${pt.name_de}${pt.description_de ? ' – ' + pt.description_de : ''}` : `${pt.name_en}${pt.description_en ? ' – ' + pt.description_en : ''}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className='col-md-6'>
                        <label className='form-label'>{t('Chargiergewicht [t]', 'Charge weight [t]')}</label>
                        <input
                          className='form-control'
                          type='text'
                          inputMode='decimal'
                          pattern='[0-9]*[.,]?[0-9]*'
                          value={form.plants[selectedPlantIndex].chargeWeightTons ?? ''}
                          onKeyDown={(e) => {
                            const invalid = ['e', 'E', '+', '-'];
                            if (invalid.includes(e.key)) e.preventDefault();
                          }}
                          onChange={(e) => {
                            const raw = e.target.value || '';
                            const sanitized = raw.replace(/[^0-9.,]/g, '');
                            const normalized = sanitized.replace(',', '.');
                            updateSelectedPlant({
                              chargeWeightTons: normalized === '' ? undefined : Number(normalized)
                            });
                          }}
                          placeholder={t('z. B. 25,5', 'e.g. 25.5')}
                        />
                      </div>

                      <div className='col-12'>
                        <label className='form-label'>{t('Beschreibung', 'Description')}</label>
                        <textarea className='form-control' rows={3} value={form.plants[selectedPlantIndex].description || ''} onChange={e => updateSelectedPlant({ description: e.target.value })} />
                      </div>

                      {/* Icon picker */}
                      <div className='col-12'>
                        <label className='form-label'>{t('Icon der Anlage', 'Plant icon')}</label>
                        {icons.length > 0 ? (
                          <div className='d-flex flex-wrap gap-2 border rounded p-2' style={{ maxHeight: 200, overflowY: 'auto' }}>
                            {icons.map(ic => {
                              const selected = form.plants[selectedPlantIndex].icon === ic.name || form.plants[selectedPlantIndex].icon === ic.src;
                              return (
                                <button type='button' key={ic.name} className={`btn btn-sm ${selected ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => updateSelectedPlant({ icon: ic.name })} title={ic.name}>
                                  <img src={ic.src} alt={ic.name} style={{ width: 24, height: 24 }} />
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className='form-text'>{t('Keine Icon-Assets gefunden (images/PlantIcons).', 'No icon assets found (images/PlantIcons).')}</div>
                        )}
                      </div>

                      {/* Activities */}
                      <div className='col-12'>
                        <label className='form-label'>{t('Standard-Aktivitäten', 'Standard activities')}</label>
                        <div className='row'>
                          {activities.map(act => {
                            const checked = form.plants[selectedPlantIndex].activities.includes(act.internalName);
                            const label = language === 'de' ? (act.shortName || act.name_de) : (act.shortName || act.name_en || act.name_de);
                            return (
                              <div className='col-md-6' key={act.internalName}>
                                <div className='form-check mb-2'>
                                  <input className='form-check-input' type='checkbox' id={`act_${act.internalName}`} checked={checked} onChange={() => toggleActivity(act.internalName)} />
                                  <label className='form-check-label' htmlFor={`act_${act.internalName}`}>
                                    {label}
                                    <small className='text-muted ms-1'>
                                      {(language === 'de' ? act.description_de : act.description_en) || ''}
                                    </small>
                                  </label>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className='text-white-50'>{t('Bitte eine Anlage links auswählen oder hinzufügen.', 'Please select or add a plant on the left.')}</div>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className='d-flex justify-content-center my-4'>
            <button type='button' className='btn btn-danger btn-lg' onClick={onSubmit} disabled={loading}>
              {loading ? t('Wird gespeichert…', 'Saving…') : t('Konfiguration senden', 'Submit configuration')}
            </button>
          </div>
          
          
          
          </>}

        </div>
      </div>
      <Footer />
    </div>
  );
}

export default SetupPage;
